// 결과 화면 state — `/subscriptions/result?state=` 로 producer(콜백·주문완료 등)가 결과 메시지·구분을
// 인코딩해 싣고, 결과 페이지가 디코딩해 표시한다. 버튼·이미지는 `status` 로 페이지가 결정(state 엔 미포함).
// 인/디코딩은 범용 `url-state` 코덱 위에 타입 가드만 얹는다.
import { decodeUrlState, encodeUrlState } from './url-state';

/**
 * 결과 구분 — 이미지·버튼셋의 1차 분기.
 *  - `success`: 완료(이미지 `payment_success`). 세부는 `kind`.
 *  - `failed`: 실패(이미지 `payment_failed`) = 채널톡 문의 + 뒤로가기.
 *  - `pending`: 처리 대기(이미지 `contact_support`) — 일시납 등 담당자 상담 후 결제. 실패 아님이나
 *    버튼셋은 failed 와 동일(채널톡 문의 + 뒤로가기). 결제화면 복귀(`redirectUrl`) 안내용.
 */
export type ResultStatus = 'success' | 'failed' | 'pending';

/**
 * 성공 화면 버튼셋 구분(`status:'success'` 일 때만).
 *  - `subscription`(기본): 구독 완료 = 다른 발전소/앱/홈.
 *  - `registration`: 결제수단 등록 완료(≠ 결제완료) = 돌아가기(결제화면 복귀해 결제 이어감).
 */
export type ResultKind = 'subscription' | 'registration';

export interface ResultState {
  /** 제목(큰 굵은 텍스트). */
  title: string;
  /** 보조 문구(제목 아래). */
  subTitle: string;
  /** 결과 구분 — 이미지·버튼셋을 가른다. */
  status: ResultStatus;
  /** 성공 화면 버튼셋 구분(선택, 기본 `subscription`). success 외엔 무관. */
  kind?: ResultKind;
  /** 뒤로가기 목적지 풀 URL(선택) — 결제 진입 화면. 없으면 홈으로. */
  redirectUrl?: string;
  /**
   * 뒤로가기 버튼 자체를 숨긴다(선택, 기본 노출).
   *
   * **돌아갈 곳이 없는 안내**에 쓴다 — 예: 결제 비활성(`tossPaymentGuard`)으로 퍼널이 통째로 닫혀 이 화면에
   * 온 경우. 되돌아가면 가드가 다시 여기로 보내므로 버튼이 무의미하고, 홈 폴백으로 흘려보내면 사용자를
   * 요청하지도 않은 화면으로 밀어내게 된다.
   *
   * `redirectUrl` 부재로 판정하지 않는 이유: 그 부재는 이미 "홈으로 폴백"이라는 다른 의미를 갖고 있고
   * (콜백 실패 경로가 그걸 쓴다), 의미를 겹쳐 태우면 한쪽을 고칠 때 다른 쪽이 조용히 바뀐다.
   */
  hideBack?: boolean;
}

/** state → URL-safe 문자열(`?state=` 직삽 가능). */
export function encodeResultState(state: ResultState): string {
  return encodeUrlState(state);
}

/** URL-safe 문자열 → state. 깨진 값·알 수 없는 `status` 는 `null`(호출처가 폴백 처리). */
export function decodeResultState(raw: string | null): ResultState | null {
  const state = decodeUrlState<ResultState>(raw);
  const valid =
    state?.status === 'success' || state?.status === 'failed' || state?.status === 'pending';
  return valid ? state : null;
}
